import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import MyPiggyIcon from '../UI/MyPiggyIcon';


const TOUR_STEPS = [
  {
    key: 'manage_family',
    selector: '[data-tour="manage-family"]',
    icon: 'family',
    title: 'Manage Family',
    description:
      'Manage your family name, children, and adult access. Open Manage Child from here or a dashboard child card for profile editing, goals, chore approval, password changes, and deactivation. Each child’s Login ID stays the same.',
    guardianDescription:
      'View family information and open Manage Child to edit child profiles, review goals, set chore approval, or change passwords. Subscription, shared time zone, and adult invitations are managed by the Family Organizer.',
  },
  {
    key: 'chores',
    selector: '[data-tour="chores"]',
    icon: 'do-chores',
    title: 'Manage Chores',
    description:
      'Create, search, filter, and edit chores in a compact list. Assign a chore to all kids or one child. Set each child’s approval preference under Manage Child; review completed work from the next shortcut.',
  },
  {
    key: 'review_completed_chores',
    selector: '[data-tour="review-completed-chores"]',
    icon: 'chores-submitted-for-approval',
    title: 'Review Completed Chores',
    description:
      'Review chores your kids marked as completed. The badge shows how many are waiting for your approval. Approving adds the linked reward to Available Money; rejecting leaves the balance unchanged. Only children with Require Parent Approval for Chores enabled appear here.',
  },
  {
    key: 'reports',
    selector: '[data-tour="reports"]',
    icon: 'view-my-reports',
    title: 'Family Reports',
    description:
      'See money-in and money-out charts, savings progress, and family money habits. Open View All Transactions from the report for detailed activity and CSV export with the report’s filters.',
  },
  {
    key: 'transactions',
    selector: '[data-tour="transactions"]',
    icon: 'transaction-history',
    title: 'View All Transactions',
    description:
      'Review compact family activity, filter by child, date, or activity type, search transactions, and export the matching records as CSV.',
  },
  {
    key: 'pocket_tip',
    selector: '[data-tour="pocket-tip"]',
    icon: 'learning-tip',
    title: 'Notifications & Pocket Tips',
    description:
      'Open the bell for Today’s Pocket Tip and family activity summaries. Tap a notification to read it; daily tips now live here instead of taking space on the dashboard.',
  },
  {
    key: 'parent_settings',
    selector: '[data-tour="parent-settings"]',
    icon: 'settings',
    title: 'Parent Settings',
    description:
      'Manage your profile, subscription, email summaries, appearance, and family time zone. Find the Parents and Kids app download cards here, and replay this tour anytime.',
    guardianDescription:
      'Manage your profile, email summaries, and appearance. View the family’s subscription and time zone; only the Family Organizer can change those. Find both app download cards and replay this tour in Settings.',
  },
];


const getTargetRect = (selector) => {
  const element =
    document.querySelector(selector);

  if (!element) {
    return null;
  }

  const rect =
    element.getBoundingClientRect();

  return {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
  };
};


export default function ParentDashboardTour({
  open,
  initialStepKey = 'manage_family',
  viewerRole = 'owner',
  onStepChange,
  onFinish,
  onExit,
}) {
  const initialIndex = Math.max(
    0,
    TOUR_STEPS.findIndex(
      (item) =>
        item.key === initialStepKey
    )
  );

  const [
    stepIndex,
    setStepIndex,
  ] = useState(initialIndex);

  const [
    targetRect,
    setTargetRect,
  ] = useState(null);

  const [
    cardHeight,
    setCardHeight,
  ] = useState(220);

  const [finishing, setFinishing] = useState(false);

  const cardRef =
    useRef(null);

  const step =
    TOUR_STEPS[stepIndex];

  const isFirst =
    stepIndex === 0;

  const isLast =
    stepIndex ===
    TOUR_STEPS.length - 1;

  const cardPosition =
    useMemo(() => {
      const viewportPadding = 16;
      const targetGap = 14;
      const cardWidth = Math.min(
        448,
        window.innerWidth -
          viewportPadding * 2
      );

      if (!targetRect) {
        return {
          top: '50%',
          left: '50%',
          width: cardWidth,
          transform:
            'translate(-50%, -50%)',
        };
      }

      const targetBottom =
        targetRect.top +
        targetRect.height;

      const spaceBelow =
        window.innerHeight -
        targetBottom -
        targetGap -
        viewportPadding;

      const spaceAbove =
        targetRect.top -
        targetGap -
        viewportPadding;

      let top;

      if (spaceBelow >= cardHeight) {
        top =
          targetBottom +
          targetGap;
      } else if (
        spaceAbove >= cardHeight
      ) {
        top =
          targetRect.top -
          targetGap -
          cardHeight;
      } else if (
        spaceBelow >= spaceAbove
      ) {
        top = Math.min(
          targetBottom + targetGap,
          window.innerHeight -
            cardHeight -
            viewportPadding
        );
      } else {
        top = Math.max(
          viewportPadding,
          targetRect.top -
            targetGap -
            cardHeight
        );
      }

      top = Math.max(
        viewportPadding,
        Math.min(
          top,
          window.innerHeight -
            cardHeight -
            viewportPadding
        )
      );

      const centeredLeft =
        targetRect.left +
        targetRect.width / 2 -
        cardWidth / 2;

      const left = Math.max(
        viewportPadding,
        Math.min(
          centeredLeft,
          window.innerWidth -
            cardWidth -
            viewportPadding
        )
      );

      return {
        top,
        left,
        width: cardWidth,
      };
    }, [
      cardHeight,
      targetRect,
    ]);


  useEffect(() => {
    if (open) {
      setStepIndex(initialIndex);
    }
  }, [initialIndex, open]);


  useEffect(() => {
    if (!open || !step) {
      return undefined;
    }

    const target =
      document.querySelector(
        step.selector
      );

    target?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest',
    });

    const updateRect = () => {
      setTargetRect(
        getTargetRect(step.selector)
      );
    };

    updateRect();

    const settleTimer =
      window.setTimeout(
        updateRect,
        400
      );

    window.addEventListener(
      'resize',
      updateRect
    );

    window.addEventListener(
      'scroll',
      updateRect,
      true
    );

    onStepChange?.(
      step.key,
      stepIndex
    );

    return () => {
      window.clearTimeout(
        settleTimer
      );

      window.removeEventListener(
        'resize',
        updateRect
      );

      window.removeEventListener(
        'scroll',
        updateRect,
        true
      );
    };
  }, [
    onStepChange,
    open,
    step,
    stepIndex,
  ]);


  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && !finishing) {
        onExit?.();
      }
    };

    window.addEventListener(
      'keydown',
      handleKeyDown
    );

    return () =>
      window.removeEventListener(
        'keydown',
        handleKeyDown
      );
  }, [finishing, onExit, open]);


  useEffect(() => {
    if (!open || !cardRef.current) {
      return undefined;
    }

    const updateCardHeight = () => {
      const nextHeight =
        cardRef.current
          ?.getBoundingClientRect()
          .height;

      if (nextHeight) {
        setCardHeight(nextHeight);
      }
    };

    updateCardHeight();

    const observer =
      new ResizeObserver(
        updateCardHeight
      );

    observer.observe(
      cardRef.current
    );

    return () =>
      observer.disconnect();
  }, [
    open,
    stepIndex,
  ]);


  if (!open || !step) {
    return null;
  }


  const goBack = () => {
    setStepIndex(
      (current) =>
        Math.max(0, current - 1)
    );
  };


  const finishTour = async (viewApps = false) => {
    if (finishing) return;
    setFinishing(true);
    try { await onFinish?.({ viewApps }); }
    finally { setFinishing(false); }
  };

  const goForward = () => {
    if (isLast) {
      finishTour();
      return;
    }

    setStepIndex(
      (current) =>
        Math.min(
          TOUR_STEPS.length - 1,
          current + 1
        )
    );
  };


  return (
    <div
      className="mypiggy-brand fixed inset-0 z-[200]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="parent-tour-title"
      aria-describedby="parent-tour-description"
    >
      {!targetRect && (
        <div className="absolute inset-0 bg-slate-950/70" />
      )}

      {targetRect && (
        <div
          className="fixed rounded-xl ring-4 ring-white shadow-[0_0_0_9999px_rgba(15,23,42,0.72)] pointer-events-none transition-all duration-200"
          style={{
            top: Math.max(
              6,
              targetRect.top - 6
            ),
            left: Math.max(
              6,
              targetRect.left - 6
            ),
            width: Math.min(
              window.innerWidth - 12,
              targetRect.width + 12
            ),
            height:
              targetRect.height + 12,
          }}
          aria-hidden="true"
        />
      )}

      <div
        ref={cardRef}
        className="fixed max-h-[calc(100dvh-2rem)] overflow-y-auto max-w-md rounded-2xl border border-white/30 bg-white dark:bg-slate-900 p-5 shadow-2xl transition-[top,left] duration-200"
        style={cardPosition}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-xs font-bold uppercase tracking-wide text-blue-600 dark:text-blue-300">
              Dashboard Tour · Step {stepIndex + 1} of {TOUR_STEPS.length}
            </div>

            <h2
              id="parent-tour-title"
              className="mt-2 text-xl font-bold text-gray-900 dark:text-slate-100"
            >
              <MyPiggyIcon
                name={step.icon}
                size="sm"
                className="mr-2 align-middle"
              />
              {step.title}
            </h2>
          </div>

          <button
            type="button"
            onClick={onExit}
            disabled={finishing}
            className="shrink-0 rounded-full bg-gray-100 dark:bg-slate-800 w-9 h-9 text-gray-600 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-700 font-bold"
            aria-label="Exit dashboard tour"
            title="Exit Tour"
          >
            ×
          </button>
        </div>

        <p
          id="parent-tour-description"
          className="mt-3 text-sm leading-relaxed text-gray-600 dark:text-slate-300"
        >
          {viewerRole === 'guardian' &&
          step.guardianDescription
            ? step.guardianDescription
            : step.description}
        </p>

        {isLast && <div className="mt-4 rounded-xl border border-teal-200 bg-teal-50 p-3 dark:border-teal-800 dark:bg-teal-950/40">
          <p className="text-sm font-bold text-teal-900 dark:text-teal-200">Take MyPiggy with you</p>
          <p className="mt-1 text-xs text-teal-900 dark:text-teal-200">Parents and Kids have separate iOS and Android app cards in Settings. Store links appear as each app becomes available.</p>
          <button type="button" disabled={finishing} onClick={() => finishTour(true)} className="mt-2 rounded-lg bg-teal-700 px-3 py-2 text-sm font-extrabold text-white hover:bg-teal-800 disabled:opacity-50">Finish &amp; View Apps</button>
        </div>}
        <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={onExit}
            disabled={finishing}
            className="text-sm font-semibold text-gray-500 dark:text-slate-400 hover:text-gray-800 dark:hover:text-slate-200"
          >
            Exit Tour
          </button>

          <div className="flex gap-2">
            {!isFirst && (
              <button
                type="button"
                onClick={goBack}
                disabled={finishing}
                className="rounded-lg border border-gray-300 dark:border-slate-600 px-4 py-2 text-sm font-semibold text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800"
              >
                Back
              </button>
            )}

            <button
              type="button"
              onClick={goForward}
              disabled={finishing}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900"
            >
              {finishing ? 'Finishing…' : isLast
                ? 'Finish Tour'
                : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


export {
  TOUR_STEPS,
};

